import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { XIcon } from "lucide-react";
import Tooltip from "../../../../Tooltip";
import type { MultipleAnchorProps } from "./types";

const TAG_GAP = 4;
const MORE_BADGE_WIDTH = 70;

const Tag = forwardRef<
    HTMLSpanElement,
    { disabled?: boolean; label: string; onRemove: () => void }
>(({ disabled, label, onRemove }, ref) => (
    <span
        ref={ref}
        className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-2 tw-rounded-full tw-border tw-py-0.5 tw-px-1.5 tw-text-xs tw-font-medium tw-text-foreground tw-gap-2"
    >
        {label}
        <button
            type="button"
            className="tw-rounded-sm tw-text-muted-foreground hover:tw-text-foreground"
            disabled={disabled}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
                e.stopPropagation();
                onRemove();
            }}
        >
            <XIcon className="tw-size-3" />
        </button>
    </span>
));

function Multiple({
    multipleSelected,
    setOpen,
    placeholder,
    disabled,
    dateFormat,
    onChange,
}: MultipleAnchorProps) {
    const dates = useMemo(() => multipleSelected ?? [], [multipleSelected]);
    const hasValue = dates.length > 0;

    const containerRef = useRef<HTMLSpanElement>(null);
    const tagRef = useRef<HTMLSpanElement>(null);
    const [visibleCount, setVisibleCount] = useState(dates.length);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const tag = tagRef.current;
        if (!container || !tag || dates.length === 0) {
            setVisibleCount(dates.length);
            return;
        }

        const recalculate = () => {
            const tagWidth = tag.offsetWidth + TAG_GAP;
            const availableWidth = container.clientWidth;
            if (Math.floor(availableWidth / tagWidth) >= dates.length) {
                setVisibleCount(dates.length);
                return;
            }
            const withBadge = Math.floor(
                (availableWidth - MORE_BADGE_WIDTH) / tagWidth,
            );
            setVisibleCount(Math.max(1, withBadge));
        };

        recalculate();
        const observer = new ResizeObserver(recalculate);
        observer.observe(container);
        return () => observer.disconnect();
    }, [dates]);

    const visibleDates = dates.slice(0, visibleCount);
    const hiddenDates = dates.slice(visibleCount);

    const removeDate = (date: Date) => {
        onChange?.(dates.filter((d) => d.getTime() !== date.getTime()));
    };

    return (
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={() => !disabled && setOpen(true)}
            onFocus={() => !disabled && setOpen(true)}
            onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
            }}
            className="tw-flex tw-h-9 tw-w-full tw-min-w-0 tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-input tw-bg-transparent tw-py-1 tw-pr-14 tw-pl-2.5 tw-text-base tw-shadow-sm tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring md:tw-text-sm aria-disabled:tw-pointer-events-none aria-disabled:tw-cursor-not-allowed aria-disabled:tw-opacity-50"
        >
            {hasValue ? (
                <span
                    ref={containerRef}
                    className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-nowrap tw-items-center tw-gap-1 tw-overflow-hidden"
                >
                    {visibleDates.map((date, i) => (
                        <Tag
                            key={date.getTime()}
                            ref={i === 0 ? tagRef : undefined}
                            disabled={disabled}
                            label={moment(date).format(dateFormat)}
                            onRemove={() => removeDate(date)}
                        />
                    ))}
                    {hiddenDates.length > 0 && (
                        <Tooltip
                            side="bottom"
                            align="start"
                            trigger={`+${hiddenDates.length} more`}
                        >
                            <span className="tw-flex tw-max-w-48 tw-flex-col tw-gap-0.5">
                                {hiddenDates.map((date) => (
                                    <span
                                        key={date.getTime()}
                                        className="tw-truncate"
                                    >
                                        {moment(date).format(dateFormat)}
                                    </span>
                                ))}
                            </span>
                        </Tooltip>
                    )}
                </span>
            ) : (
                <span className="tw-text-muted-foreground">
                    {placeholder || dateFormat}
                </span>
            )}
        </div>
    );
}

export default Multiple;
